const initialState = {
  currentStep: 0,
  completed: false,
  inProgress: false,
  isProfileComplete: false,
  userId: '',
  lastViewedSection: null,
  paymentCompleted: false,
  totalSteps: 7, 
  profile: {
    user: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    organization: {
      organizationName: '',
      industry: '',
      size: '',
      website: '',
    },
  },
  useCase: '',
  integrations: [],
  botConfig: {
    name: '',
    voice: {
      language: 'en-US',
      voice: '',
      speed: 1,
      pitch: 0,
      volumeGainDb: 0,
    },
    personality: {
      friendliness: 5,
      formality: 5,
      humor: 5,
      empathy: 5,
      useEmojis: false,
      customPersonality: '',
    },
    prompts: [],
    behavior: {
      useSmallTalk: true,
      maxResponseTime: 5,
      handleMultipleIntents: true,
      fallbackBehavior: '',
      customBehaviorRules: '',
    },
    language: 'en',
  },
  testingResult: null,
  pricing: {
    plan: '',
    price: 0,
  },
  aiTalkerConfig: {
    personality: '',
    voice: '',
    language: '',
    behavior: '',
  },
};

const onboardingReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      return { 
        ...state, 
        currentStep: action.payload.step, 
        inProgress: true 
      };
    case 'SET_USER_PROFILE':
      return {
        ...state,
        profile: {
          ...state.profile,
          user: { ...state.profile.user, ...(action.payload.user || {}) },
          organization: { ...state.profile.organization, ...(action.payload.organization || {}) }
        },
        userId: action.payload.user?.userId || state.userId,
        inProgress: true
      };
    case 'SET_USER_ID':
      return {
        ...state,
        userId: action.payload,
        profile: {
          ...state.profile,
          user: { ...state.profile.user, userId: action.payload }
        },
        inProgress: true
      };
    case 'SET_ORGANIZATION_PROFILE':
      return {
        ...state,
        profile: {
          ...state.profile,
          organization: { ...state.profile.organization, ...action.payload }
        },
        inProgress: true
      };
    case 'SET_ORGANIZATION_NAME':
      return {
        ...state,
        profile: {
          ...state.profile,
          organization: { ...state.profile.organization, organizationName: action.payload }
        },
        inProgress: true
      };
    case 'SET_INDUSTRY':
      return {
        ...state,
        profile: {
          ...state.profile,
          organization: { ...state.profile.organization, industry: action.payload }
        },
        inProgress: true
      };
    case 'SET_SIZE':
      return {
        ...state,
        profile: {
          ...state.profile,
          organization: { ...state.profile.organization, size: action.payload }
        },
        inProgress: true
      };
    case 'SET_WEBSITE':
      return {
        ...state,
        profile: {
          ...state.profile,
          organization: { ...state.profile.organization, website: action.payload }
        },
        inProgress: true
      };
    case 'SET_PROFILE_COMPLETE':
      return { ...state, isProfileComplete: action.payload };
    case 'SET_USE_CASE':
      return { ...state, useCase: action.payload, inProgress: true };
    case 'SET_INTEGRATIONS':
      return { ...state, integrations: action.payload, inProgress: true };
    case 'SET_BOT_CONFIG':
      const newBotConfig = {
        ...state.botConfig,
        ...action.payload,
        voice: {
          ...state.botConfig.voice,
          ...action.payload.voice,
        },
      };
      return {
        ...state,
        botConfig: newBotConfig,
        inProgress: true
      };
    case 'SET_TESTING_RESULT':
      return { ...state, testingResult: action.payload, inProgress: true };
    case 'SET_PRICING':
      return { ...state, pricing: action.payload, inProgress: true };
    case 'SET_AI_TALKER_CONFIG':
      return { ...state, aiTalkerConfig: action.payload, inProgress: true };
    case 'SET_ONBOARDING_DATA':
      return {
        ...state,
        profile: { ...state.profile, ...action.payload.profileData },
        aiTalkerConfig: { ...state.aiTalkerConfig, ...action.payload.aiTalkerConfig },
        inProgress: true
      };
    case 'COMPLETE_ONBOARDING':
      return { ...state, completed: true, inProgress: false };
    case 'SET_ONBOARDING_STATE':
      return { ...state, ...action.payload, inProgress: true };
    case 'RESET_ONBOARDING':
      return { ...initialState };
    case 'UPDATE_VOICE_CONFIG':
      return {
        ...state,
        botConfig: {
          ...state.botConfig,
          voice: {
            ...state.botConfig.voice,
            ...action.payload,
          },
        },
      };
    case 'SET_LAST_VIEWED_SECTION':
      return { ...state, lastViewedSection: action.payload };
    case 'SET_PAYMENT_COMPLETED':
      return { ...state, paymentCompleted: action.payload };
    case 'SET_ONBOARDING_COMPLETE':
      return { ...state, completed: true, inProgress: false };
    default:
      return state;
  }
};

// Add these action creators
export const setPaymentCompleted = (isCompleted) => ({
  type: 'SET_PAYMENT_COMPLETED',
  payload: isCompleted,
});

export const setOnboardingComplete = () => ({
  type: 'SET_ONBOARDING_COMPLETE',
});

// Add this action creator
export const setLastViewedSection = (section) => ({
  type: 'SET_LAST_VIEWED_SECTION',
  payload: section,
});

// Action creators
export const setCurrentStep = (step) => {
  return (dispatch) => {
    dispatch({
      type: 'SET_CURRENT_STEP',
      payload: { step },
    });
    dispatch(saveOnboardingState());
  };
};

export const setUserProfile = (userProfile) => ({
  type: 'SET_USER_PROFILE',
  payload: userProfile,
});

export const setUserId = (userId) => ({
  type: 'SET_USER_ID',
  payload: userId,
});

export const setOrganizationProfile = (orgProfile) => ({
  type: 'SET_ORGANIZATION_PROFILE',
  payload: orgProfile,
});

export const setProfileComplete = (isComplete) => ({
  type: 'SET_PROFILE_COMPLETE',
  payload: isComplete,
});

export const setUseCase = (useCase) => ({
  type: 'SET_USE_CASE',
  payload: useCase,
});

export const setIntegrations = (integrations) => {
  return async (dispatch) => {
    dispatch({
      type: 'SET_INTEGRATIONS',
      payload: integrations,
    });
    dispatch(saveOnboardingState());
  };
};

export const setBotConfig = (botConfig) => {
  return async (dispatch) => {
    dispatch({
      type: 'SET_BOT_CONFIG',
      payload: botConfig,
    });
    dispatch(saveOnboardingState());
  };
};

export const setTestingResult = (result) => ({
  type: 'SET_TESTING_RESULT',
  payload: result,
});

export const setPricing = (pricing) => ({
  type: 'SET_PRICING',
  payload: pricing,
});

export const setAITalkerConfig = (config) => ({
  type: 'SET_AI_TALKER_CONFIG',
  payload: config,
});

export const setOnboardingData = (data) => ({
  type: 'SET_ONBOARDING_DATA',
  payload: data,
});

export const completeOnboarding = () => ({
  type: 'COMPLETE_ONBOARDING',
});

export const resetOnboarding = () => ({
  type: 'RESET_ONBOARDING',
});

export const saveOnboardingState = () => {
  return async (dispatch, getState) => {
    try {
      const state = getState().onboarding;
      localStorage.setItem('onboardingState', JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save onboarding state:', error);
      throw error;
    }
  };
};

export const loadOnboardingState = () => {
  return async (dispatch) => {
    try {
      const savedState = localStorage.getItem('onboardingState');
      const savedProgress = localStorage.getItem('onboardingProgress');
      
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (savedProgress) {
          const { currentStep } = JSON.parse(savedProgress);
          parsedState.currentStep = currentStep;
        }
        dispatch({
          type: 'SET_ONBOARDING_STATE',
          payload: parsedState,
        });
      } else if (savedProgress) {
        const { currentStep } = JSON.parse(savedProgress);
        dispatch({
          type: 'SET_CURRENT_STEP',
          payload: { step: currentStep },
        });
      }
    } catch (error) {
      console.error('Failed to load onboarding state:', error);
    }
  };
};

export const updateVoiceConfig = (voiceConfig) => {
  return async (dispatch) => {
    dispatch({
      type: 'UPDATE_VOICE_CONFIG',
      payload: voiceConfig,
    });
    dispatch(saveOnboardingState());
  };
};

export default onboardingReducer;
